import { color } from "@repo/fika-design-system/color"
import { createIntervalCounter } from "@solid-primitives/timer"
import { useNavigate } from "@solidjs/router"
import { createMemo, createResource, For, Show } from "solid-js"
import { urqlClient } from "@/context/graphql-provider"
import { Box } from "@/designSystem/components/box"
import { Icon } from "@/designSystem/components/icon"
import { Mini } from "@/designSystem/components/mini/mini"
import { Tooltip } from "@/designSystem/components/tooltip"
import { MainLayout } from "@/designSystem/layouts/main-layout"
import { Navigation } from "@/designSystem/patterns/navigation"
import { PublicationTabs } from "@/designSystem/patterns/publication-tabs"
import { createFragment, graphql } from "@/graphql"
import { useI18nContext } from "@/i18n/i18n-solid"
import {
  createBlogRouter,
  INTERVAL,
  type Interval,
  type SortField,
} from "@/routes/posts-router"
import {
  cellRecipe,
  layoutClass,
  paginationButtonClass,
  paginationClass,
  paginationIconClass,
  percentageClass,
  rowRecipe,
  tableBodyClass,
  tableClass,
} from "./stats.css"

const PAGE_SIZE = 10

const TableStatsFragment = graphql(`
  fragment TableStatsFragment on Query {
    me {
      id
      publication {
        id
        referrerStats(interval: $interval) {
          label
          visitors
          pageviews
        }
        countryStats(interval: $interval) {
          label
          visitors
          pageviews
        }
        channelStats(interval: $interval) {
          label
          visitors
          pageviews
        }
      }
    }
  }
`)

const TableStatsQuery = graphql(
  `query TableStats($interval: String!) {
    ...TableStatsFragment
  }`,
  [TableStatsFragment],
)

export function Stats() {
  const navigate = useNavigate()
  const { LL } = useI18nContext()
  const { paths, statsParams } = createBlogRouter()

  const interval = createMemo(() => statsParams().interval)

  const [queryResult] = createResource(interval, async interval => {
    const result = await urqlClient
      .query(TableStatsQuery, { interval })
      .toPromise()
    return result.data
  })

  const data = createFragment(TableStatsFragment, () => queryResult() ?? null)

  const byReferrer = createMemo(() => {
    const publication = data()?.me?.publication
    const stats = publication?.referrerStats
    if (!stats) return []
    return stats.map(
      item => [item.label, item.visitors, item.pageviews] satisfies TableData,
    )
  })

  const byCountry = createMemo(() => {
    const publication = data()?.me?.publication
    const stats = publication?.countryStats
    if (!stats) return []
    return stats.map(item => [item.label, item.visitors] satisfies TableData)
  })

  const byChannel = createMemo(() => {
    const publication = data()?.me?.publication
    const stats = publication?.channelStats
    if (!stats) return []
    return stats.map(item => [item.label, item.visitors] satisfies TableData)
  })

  return (
    <MainLayout
      hue="amber"
      header={<Navigation hue="amber" title={LL().blog.title()} />}
    >
      <Box gap="12">
        <PublicationTabs />
        <Mini.Menu>
          <Tooltip description={LL().blog.interval()}>
            <Mini.Select
              fixedIcon="calendar-clock"
              items={Object.values(INTERVAL).map(interval => ({
                value: interval,
                label: interval,
              }))}
              value={INTERVAL[statsParams().interval]}
              onChange={interval => {
                navigate(
                  paths().stats({
                    interval: Object.keys(INTERVAL).find(
                      key => INTERVAL[key as Interval] === interval,
                    ) as Interval,
                  }),
                )
              }}
            />
          </Tooltip>
        </Mini.Menu>
        <div class={layoutClass}>
          <Show
            when={!queryResult.loading}
            fallback={
              <>
                <LoadingTable field="referrer" withViews />
                <LoadingTable field="country" />
                <LoadingTable field="channel" />
              </>
            }
          >
            <Table field="referrer" data={byReferrer()} />
            <Table field="country" data={byCountry()} />
            <Table field="channel" data={byChannel()} />
          </Show>
        </div>
      </Box>
    </MainLayout>
  )
}

function LoadingTable(props: { field: SortField; withViews?: boolean }) {
  const count = createIntervalCounter(100)
  const spinner = "⠁⠂⠄⡀⢀⠠⠐⠈".split("")
  const tuples = createMemo(() => {
    const str = spinner[count() % spinner.length]
    return Array.from({ length: PAGE_SIZE }, (_, _i) => {
      return props.withViews ? [str, null, null] : [str, null]
    })
  })

  return <Table field="referrer" data={tuples() as TableData[]} />
}

type TableData = [string, number, number] | [string, number]

interface TableProps {
  field: SortField
  data: TableData[]
}

function Table(props: TableProps) {
  const { paths, statsParams } = createBlogRouter()
  const { LL } = useI18nContext()
  const params = createMemo(() => statsParams()[props.field])

  function getPaginationUrl(direction: "next" | "prev") {
    const currentPage = params().page
    const newPage =
      direction === "next" ? currentPage + 1 : Math.max(1, currentPage - 1)

    return paths().stats({
      [props.field]: {
        page: newPage,
      },
    })
  }

  const totalVisitors = createMemo(() =>
    props.data.reduce((acc, item) => acc + item[1], 0),
  )
  const totalViews = createMemo(() =>
    props.data.reduce((acc, item) => acc + (item[2] ?? 0), 0),
  )

  const paginatedData = createMemo(() => {
    const start = (params().page - 1) * PAGE_SIZE
    return props.data.slice(start, start + PAGE_SIZE)
  })

  const hasNextPage = createMemo(() => {
    const start = params().page * PAGE_SIZE
    return start < props.data.length
  })

  const hasPrevPage = createMemo(() => params().page > 1)
  const hasViews = createMemo(() => {
    const length = props.data[0]?.length
    if (!length) return false
    return length > 2
  })
  const fieldLabels = {
    referrer: () => LL().blog.referrer(),
    country: () => LL().blog.country(),
    channel: () => LL().blog.channel(),
  } as const
  const field = createMemo(() => fieldLabels[props.field]())

  return (
    <div class={tableClass}>
      <div class={tableBodyClass}>
        <div class={rowRecipe({ header: true })}>
          <div class={cellRecipe({ wide: true })}>{field()}</div>
          <span class={cellRecipe({ wide: false })}>
            {LL().blog.visitors()}
          </span>
          <Show when={hasViews()}>
            <span class={cellRecipe({ wide: false })}>{LL().blog.views()}</span>
          </Show>
        </div>
        <For each={paginatedData()}>
          {item => (
            <Row
              value={item[0]}
              views={item[2]}
              visitors={item[1]}
              totalViews={totalViews()}
              totalVisitors={totalVisitors()}
            />
          )}
        </For>
      </div>
      <Show when={props.data.length > PAGE_SIZE}>
        <div class={paginationClass}>
          <a
            href={hasPrevPage() ? getPaginationUrl("prev") : undefined}
            class={paginationButtonClass}
            aria-disabled={!hasPrevPage()}
          >
            <Icon class={paginationIconClass} name="arrow-left" size="18" />
          </a>
          <a
            href={hasNextPage() ? getPaginationUrl("next") : undefined}
            class={paginationButtonClass}
            aria-disabled={!hasNextPage()}
          >
            <Icon class={paginationIconClass} name="arrow-right" size="18" />
          </a>
        </div>
      </Show>
    </div>
  )
}

interface RowProps {
  value: string
  views?: number
  visitors: number
  totalViews: number
  totalVisitors: number
}

function Row(props: RowProps) {
  const visitorPercentage = createMemo(() => {
    if (props.totalVisitors === 0) return ""

    return ((props.visitors / props.totalVisitors) * 100).toFixed(1)
  })

  function viewPercentage(views: number) {
    if (props.totalViews === 0) return 0

    return ((views / props.totalViews) * 100).toFixed(1)
  }

  return (
    <div class={rowRecipe({ header: false })}>
      <div
        style={{
          background: `linear-gradient(90deg, ${color.amber200} ${visitorPercentage()}%, transparent ${visitorPercentage()}%)`,
        }}
        class={cellRecipe({ wide: true })}
      >
        {props.value}
      </div>
      <Show when={props.views}>
        {views => (
          <div class={cellRecipe({ wide: false })}>
            {views()}{" "}
            <span class={percentageClass}>{viewPercentage(views())}%</span>
          </div>
        )}
      </Show>
      <Show when={props.visitors}>
        {visitors => (
          <div class={cellRecipe({ wide: false })}>
            {visitors()}{" "}
            <span class={percentageClass}>{visitorPercentage()}%</span>
          </div>
        )}
      </Show>
    </div>
  )
}
